import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'
import type { ValidRoutes } from '@/types/routes'
import type { z } from 'zod'

export function useCleanQueryStates<
  TSchema extends z.ZodTypeAny,
  T = z.infer<TSchema>,
>(from: ValidRoutes) {
  const search = useSearch({ from } as any) as T
  const navigate = useNavigate()

  const setSearch = useCallback(
    (updates: Partial<T> | ((prev: T) => Partial<T>)) => {
      const newSearch =
        typeof updates === 'function' ? updates(search) : updates

      // Merge with existing search params
      const merged = { ...search, ...newSearch }

      // Remove undefined, null, empty string, empty arrays, and false boolean values
      const cleanedSearch = Object.entries(merged).reduce(
        (acc, [key, value]) => {
          // Skip undefined and null
          if (value === undefined || value === null) {
            return acc
          }

          // Skip empty strings
          if (typeof value === 'string' && (value === '' || value === 'all')) {
            return acc
          }

          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) {
            return acc
          }

          if (Array.isArray(value) && value.every((v) => v === '')) {
            return acc
          }

          // Skip objects with all null/undefined/empty values (like date ranges)
          if (typeof value === 'object' && !Array.isArray(value)) {
            const hasValidValue = Object.values(value).some(
              (v) => v !== null && v !== undefined && v !== '',
            )
            if (!hasValidValue) {
              return acc
            }
          }

          // Skip false boolean values for filters like onlyActives
          if (typeof value === 'boolean' && value === false) {
            return acc
          }

          // Only add truthy values
          acc[key] = value
          return acc
        },
        {} as Record<string, any>,
      )

      navigate({
        search: cleanedSearch as any,
        replace: true,
      })
    },
    [search, navigate],
  )

  return [search, setSearch] as const
}
